import { useEffect, useState, useCallback } from "react";
import { Upload, FileText, X, ScanLine, AlertCircle, Plus, Trash2 } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Button } from "../ui/Button";
import { CurrencySelect } from "../ui/CurrencySelect";
import { ContactFormModal } from "../contacts/ContactFormModal";
import { resizeImage } from "../../lib/imageResize";
import { fmtCurrency, eur } from "../../lib/format";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Mode = "expense" | "payment";
type Tab = "scan" | "importNew" | "importExisting";

interface AmountLine {
  amount: string; // bedrag incl. BTW (gebruiker voert in)
  vatRateId: string; // btw-soort
}

interface ScanImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: Mode;
  /** Lijst opnieuw laden na een succesvolle actie. */
  onSaved: () => void;
  /** Open de bestaande edit-modal met de zojuist aangemaakte regel, zodat de
   *  gebruiker de door AI herkende velden kan controleren/corrigeren. */
  onEditCreated: (row: any) => void;
}

const methodOptions = [
  { value: "BANK_TRANSFER", label: "Bankoverschrijving" },
  { value: "CASH", label: "Contant" },
  { value: "IDEAL", label: "iDEAL" },
  { value: "CREDIT_CARD", label: "Creditcard" },
  { value: "PAYPAL", label: "PayPal" },
  { value: "OTHER", label: "Overige" },
];

export function ScanImportModal({ isOpen, onClose, mode, onSaved, onEditCreated }: ScanImportModalProps) {
  const { user, activeCompany } = useAuth();
  const role = user?.role;
  const pkg = activeCompany?.activePackage ?? null;
  const isPlatform = !!activeCompany?.isPlatform;
  const canScan = !!pkg?.hasOcr || isPlatform || role === "SUPERADMIN";

  const [tab, setTab] = useState<Tab>(canScan ? "scan" : "importNew");
  const [contacts, setContacts] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [existingRows, setExistingRows] = useState<any[]>([]);
  const [vatRates, setVatRates] = useState<any[]>([]);
  const [quota, setQuota] = useState<{ used: number; limit: number | null; allowed: boolean } | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [merchant, setMerchant] = useState("");
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("BANK_TRANSFER");
  const [contactId, setContactId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [existingId, setExistingId] = useState("");
  const [showContactModal, setShowContactModal] = useState(false);
  const [lines, setLines] = useState<AmountLine[]>([{ amount: "", vatRateId: "" }]);
  // Multi-valuta: valuta + wisselkoers (EUR per 1 eenheid currency).
  const [currency, setCurrency] = useState("EUR");
  const [exchangeRate, setExchangeRate] = useState<string>("1");
  const [rateLoading, setRateLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // (Her)laad afhankelijke data als de modal opent.
  useEffect(() => {
    if (!isOpen) return;
    setTab(canScan ? "scan" : "importNew");
    setError("");
    api.get("/api/contacts").then((res) => setContacts(res.data)).catch(() => {});
    api.get("/api/vat-rates").then((res) => {
      setVatRates(res.data);
      const defaultVat = res.data.find((v: any) => Number(v.percentage) === 21)?.id || res.data[0]?.id || "";
      setLines([{ amount: "", vatRateId: defaultVat }]);
    }).catch(() => {});
    if (mode === "payment") {
      api.get("/api/invoices").then((res) => setInvoices(res.data)).catch(() => {});
    }
    if (canScan) {
      api.get("/api/ocr/quota").then((res) => setQuota(res.data)).catch(() => {});
    }
    // Bestaande regels zonder document (voor import-bestaand).
    const endpoint = mode === "expense" ? "/api/expenses" : "/api/payments";
    api.get(endpoint).then((res) => setExistingRows(res.data.filter((r: any) => !r.receiptUrl))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, canScan]);

  const resetForm = () => {
    setFile(null);
    setPreviewUrl(null);
    setDate(new Date().toISOString().split("T")[0]);
    setReference("");
    setDescription("");
    setMerchant("");
    setNotes("");
    setMethod("BANK_TRANSFER");
    setContactId("");
    setInvoiceId("");
    setExistingId("");
    setError("");
    setCurrency("EUR");
    setExchangeRate("1");
    setRateLoading(false);
    setLines([{ amount: "", vatRateId: vatRates.find((v: any) => Number(v.percentage) === 21)?.id || vatRates[0]?.id || "" }]);
  };

  /** Haal de EUR-koers op voor een valuta op een datum (frankfurter). */
  const fetchRate = async (cur: string, rateDate: string) => {
    if (!cur || cur.toUpperCase() === "EUR") { setExchangeRate("1"); return; }
    setRateLoading(true);
    try {
      const { data } = await api.get("/api/exchange-rate", { params: { from: cur, date: rateDate } });
      if (typeof data?.rate === "number" && data.rate > 0) setExchangeRate(String(data.rate));
    } catch { /* laat handmatige waarde staan */ }
    finally { setRateLoading(false); }
  };
  const onCurrencyChange = (cur: string) => { setCurrency(cur); fetchRate(cur, date); };

  const close = () => {
    resetForm();
    onClose();
  };

  const handleFile = useCallback(async (selectedFile: File) => {
    setError("");
    const resized = await resizeImage(selectedFile, 720);
    setFile(resized);
    setPreviewUrl(URL.createObjectURL(resized));
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const isPdf = (url?: string | null, f?: File | null) =>
    (f && f.type === "application/pdf") || (url && url.toLowerCase().endsWith(".pdf"));

  const contactLabel = mode === "expense" ? "Leverancier" : "Klant";
  const defaultContactType = mode === "expense" ? "SUPPLIER" : "CUSTOMER";

  const vatRateOptions = [
    { value: "", label: "Geen BTW (0%)" },
    ...vatRates.map((v: any) => ({ value: v.id, label: `${Number(v.percentage)}%${v.name ? ` — ${v.name}` : ""}` })),
  ];

  /** Parse een bedrag: Nederlands ("2.178,00" → 2178) óf een JSON-nummer als
   *  string ("217.8" → 217.8 — decimale punt, dus NIET als duizendtalscheider
   *  wegstrippen). */
  const parseAmt = (s: string): number => {
    const raw = String(s).trim();
    if (/^-?\d+(\.\d{1,2})?$/.test(raw)) return parseFloat(raw) || 0;
    const n = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  const pctFor = (vatRateId: string): number => {
    const v = vatRates.find((r: any) => r.id === vatRateId);
    return v ? Number(v.percentage) : 0;
  };

  /** Netto + BTW voor één regel (bedrag is incl. BTW). */
  const lineCalc = (line: AmountLine) => {
    const incl = parseAmt(line.amount);
    const pct = pctFor(line.vatRateId);
    const netto = pct > 0 ? incl / (1 + pct / 100) : incl;
    const btw = incl - netto;
    return { incl, netto, btw };
  };

  const totals = lines.reduce(
    (acc, l) => {
      const c = lineCalc(l);
      acc.bruto += c.incl;
      acc.netto += c.netto;
      acc.btw += c.btw;
      return acc;
    },
    { bruto: 0, netto: 0, btw: 0 },
  );

  const fmtEuro = (n: number) => fmtCurrency(n, currency);
  const rateNum = Number(exchangeRate) || 1;
  const isForeign = currency && currency !== "EUR";
  const brutoEur = isForeign ? totals.bruto * rateNum : totals.bruto;

  const updateLine = (i: number, patch: Partial<AmountLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((prev) => [...prev, { amount: "", vatRateId: vatRates.find((v: any) => Number(v.percentage) === 21)?.id || vatRates[0]?.id || "" }]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  /** Bouw FormData voor scan/importNew en POST naar het juiste endpoint. */
  const submitCreate = async (skipOcr: boolean) => {
    if (!file) { setError("Upload een document (JPEG, PNG of PDF)."); return; }
    if (!skipOcr && !canScan) { setError("Scannen zit niet in jouw pakket."); return; }
    // Scan (skipOcr=false): bedrag komt uit OCR op de backend → hier niet eisen.
    // Import-nieuw (skipOcr=true): gebruiker vult de regels zelf → bedrag vereist.
    if (skipOcr && !(totals.bruto > 0)) { setError("Voer minstens één bedrag in."); return; }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("receipt", file);
      fd.append("date", date);
      fd.append("method", method);
      fd.append("reference", reference);
      fd.append("notes", notes);
      fd.append("skipOcr", String(skipOcr));
      fd.append("currency", currency || "EUR");
      // Scan: koers bepaalt de backend (auto-ophalen op de OCR-valuta); de
      // scan-tab heeft geen koers-UI, dus de default "1" is een stale
      // placeholder. Import-nieuw: gebruiker zag/fetchte de koers → meesturen.
      if (skipOcr && Number(exchangeRate) > 0) fd.append("exchangeRate", exchangeRate);
      if (contactId) fd.append("contactId", contactId);
      if (mode === "expense") {
        // Omschrijving/merchant mag altijd mee (gebruiker kan ze in scan-tab
        // niet invullen → backend valt terug op OCR-beschrijving/merchant).
        fd.append("description", description || (merchant ? `Uitgave ${merchant}` : "Geïmporteerde uitgave"));
        fd.append("merchant", merchant);
        if (skipOcr) {
          // Import-nieuw: per-regel btw uit de regel-editor (multi-btw). Stuur
          // de regels als JSON; de backend persisteert per-regel btw-soort +
          // herberekent de header-totalen. amountInclVat/vatAmount (totals)
          // gaan voor backward-compat mee als header-fallback.
          const validLines = lines
            .filter((l) => parseAmt(l.amount) > 0)
            .map((l) => ({ amountInclVat: parseAmt(l.amount), vatRateId: l.vatRateId || undefined }));
          fd.append("lines", JSON.stringify(validLines));
          fd.append("amountInclVat", String(totals.bruto.toFixed(2)));
          fd.append("vatAmount", String(totals.btw.toFixed(2)));
        }
        // Scan: stuur géén amountInclVat/vatAmount/vatRateId. De default-21%
        // uit de lege regel-editor zou een buitenlandse factuur ten onrechte
        // 21% BTW geven; de backend leidt bedragen + btw uit OCR af, of laat
        // de btw-soort leeg (vrijgesteld / 0% voor buitenland).
      } else {
        if (skipOcr) fd.append("amount", String(totals.bruto.toFixed(2)));
        // Scan: bedrag komt uit OCR op de backend (niet meesturen, anders
        // valideert de positive()-schema op "0" en faalt de scan).
        if (invoiceId) fd.append("invoiceId", invoiceId);
      }

      const url = mode === "expense" ? "/api/expenses" : "/api/payments/scan";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });

      // Reload contacts als backend er mogelijk een heeft aangemaakt.
      api.get("/api/contacts").then((res) => setContacts(res.data)).catch(() => {});
      onSaved();
      if (!skipOcr) {
        // Scan: open de edit-modal met de door AI herkende velden.
        onEditCreated(data);
      }
      close();
    } catch (err: any) {
      const reason = err?.response?.data?.reason;
      if (reason === "quota_exceeded" || reason === "no_ocr_in_package") {
        setError(err.response.data.error);
      } else {
        setError(err?.response?.data?.error || "Opslaan mislukt. Controleer de velden en probeer opnieuw.");
      }
    } finally {
      setSaving(false);
    }
  };

  /** Koppel een upload aan een bestaande regel (import-bestaand). */
  const submitAttachExisting = async () => {
    if (!existingId) { setError("Kies een bestaande regel om het document aan te koppelen."); return; }
    if (!file) { setError("Upload een document."); return; }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("receipt", file);
      const url =
        mode === "expense"
          ? `/api/expenses/${existingId}`
          : `/api/payments/${existingId}/document`;
      if (mode === "expense") {
        // PATCH /api/expenses/:id verwacht de expense-velden; haal ze uit de
        // gekozen regel zodat niets wordt leeggemaakt.
        const row = existingRows.find((r) => r.id === existingId);
        fd.append("description", row?.description || "Geïmporteerde uitgave");
        fd.append("amountInclVat", String(row?.amountInclVat ?? 0));
        fd.append("vatAmount", String(row?.vatAmount ?? 0));
        fd.append("date", row?.date ? new Date(row.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
        if (row?.contactId) fd.append("contactId", row.contactId);
        await api.patch(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSaved();
      close();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Koppelen mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const tabs: { key: Tab; label: string; show: boolean }[] = [
    { key: "scan", label: "Scannen", show: canScan },
    { key: "importNew", label: "Import (nieuwe regel)", show: true },
    { key: "importExisting", label: "Import (bestaande regel)", show: true },
  ];

  const quotaLow = quota && quota.limit !== null && quota.used >= (quota.limit ?? 0);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={close}
        title="Scan / import"
        subtitle={mode === "expense" ? "Bonnetje scannen of document importeren bij een uitgave" : "Betalingsbewijs scannen of importeren bij een betaling"}
        size="lg"
      >
        <div className="space-y-5">
          {/* Tabs — rode omlijning, geselecteerde tab groen/glossy-liquid */}
          <div className="flex gap-1.5 p-1.5 rounded-2xl border-2 border-red-500 bg-white">
            {tabs.filter((t) => t.show).map((t) => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setError(""); }}
                className={`flex-1 px-3 py-2 text-sm font-semibold rounded-xl transition-all ${
                  tab === t.key
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-liquid -translate-y-0.5"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Quota-melding (scan-tab) */}
          {tab === "scan" && quota && quota.limit !== null && (
            <div className={`text-xs px-3 py-2 rounded-xl border ${quotaLow ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
              OCR-gebruik deze maand: <strong>{quota.used}</strong>{quota.limit !== null ? ` van ${quota.limit}` : ""}{quotaLow ? " — limiet bereikt, importeren zonder OCR kan nog wel." : ""}
            </div>
          )}

          {/* Dropzone (alle tabs behalve importExisting tonen 'm prominent; importExisting ook) */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative flex flex-col items-center justify-center gap-3 w-full h-36 border-2 border-dashed rounded-2xl cursor-pointer transition-all overflow-hidden ${
              dragOver ? "border-brand-500 bg-brand-50/60" : "border-slate-300 bg-slate-50/60 hover:border-brand-400 hover:bg-brand-50/40"
            }`}
          >
            {previewUrl ? (
              <div className="relative w-full h-full">
                {isPdf(previewUrl, file) ? (
                  <div className="flex items-center justify-center h-full text-slate-500"><FileText size={56} /></div>
                ) : (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                )}
                <button
                  onClick={(e) => { e.preventDefault(); setFile(null); setPreviewUrl(null); }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-slate-900/70 text-white hover:bg-slate-900"
                >
                  <X size={22} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={44} className="text-slate-400" />
                <span className="text-sm text-slate-500 text-center px-6">
                  {tab === "scan" ? "Sleep een document hierheen om te scannen" : "Sleep een document hierheen, of klik om te uploaden"}
                  <br /><span className="text-xs">JPG, PNG of PDF — wordt automatisch verkleind tot max 720px</span>
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>

          {/* Scan-tab: minimaal, backend vult de rest en opent edit-modal */}
          {tab === "scan" && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 flex items-start gap-2">
                <ScanLine size={20} className="shrink-0 mt-0.5 text-brand-500" />
                Na het scannen wordt een nieuwe regel aangemaakt met de door AI herkende datum, bedragen, referentie en {contactLabel.toLowerCase()}. Het bewerkscherm opent automatisch zodat je alles kunt controleren en corrigeren.
              </p>
              <Select
                label={contactLabel}
                options={[
                  { value: "", label: `Automatisch herkennen` },
                  { value: "__new__", label: `＋ Nieuwe ${contactLabel.toLowerCase()} aanmaken...` },
                  ...contacts.map((c) => ({ value: c.id, label: c.name })),
                ]}
                value={contactId}
                onChange={(e) => {
                  if (e.target.value === "__new__") { setContactId(""); setShowContactModal(true); }
                  else setContactId(e.target.value);
                }}
              />
              {mode === "payment" && (
                <Select
                  label="Factuur (optioneel)"
                  options={[
                    { value: "", label: "Automatisch matchen op referentie" },
                    ...invoices.map((i) => ({ value: i.id, label: `${i.number} — € ${Number(i.total || 0).toFixed(2)}` })),
                  ]}
                  value={invoiceId}
                  onChange={(e) => setInvoiceId(e.target.value)}
                />
              )}
            </div>
          )}

          {/* Import-nieuw-tab: volledig invulbaar formulier (geen OCR) */}
          {tab === "importNew" && (
            <div className="space-y-4">
              {mode === "expense" && (
                <Input label="Omschrijving" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="bijv. Kantoorartikelen" />
              )}
              {mode === "expense" && (
                <Input label="Merchant / winkel" value={merchant} onChange={(e) => setMerchant(e.target.value)} />
              )}
              <Select
                label={contactLabel}
                options={[
                  { value: "", label: `Kies ${contactLabel.toLowerCase()}...` },
                  { value: "__new__", label: `＋ Nieuwe ${contactLabel.toLowerCase()} aanmaken...` },
                  ...contacts.map((c) => ({ value: c.id, label: c.name })),
                ]}
                value={contactId}
                onChange={(e) => {
                  if (e.target.value === "__new__") { setContactId(""); setShowContactModal(true); }
                  else setContactId(e.target.value);
                }}
              />

              {/* Multi-valuta: valuta + wisselkoers boven de bedrag-regels. */}
              <div className="grid grid-cols-2 gap-4">
                <CurrencySelect label="Valuta" value={currency} onChange={(e) => onCurrencyChange(e.target.value)} />
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Wisselkoers (EUR per 1 {currency})
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                      disabled={currency === "EUR"}
                    />
                    {rateLoading && <span className="text-xs text-slate-400">…</span>}
                  </div>
                </div>
              </div>

              {/* Bedrag-regels: meerdere bedragen met elk een btw-soort; netto + btw
                  worden automatisch berekend. */}
              <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50/40 p-3">
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 px-1">
                  <div className="col-span-4">Bedrag incl. BTW</div>
                  <div className="col-span-4">BTW-soort</div>
                  <div className="col-span-3">Netto / BTW</div>
                  <div className="col-span-1"></div>
                </div>
                {lines.map((line, i) => {
                  const c = lineCalc(line);
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={line.amount}
                          onChange={(e) => updateLine(i, { amount: e.target.value })}
                          placeholder="0,00"
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        />
                      </div>
                      <div className="col-span-4">
                        <select
                          value={line.vatRateId}
                          onChange={(e) => updateLine(i, { vatRateId: e.target.value })}
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        >
                          {vatRateOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3 text-xs text-slate-500 leading-tight">
                        <div>netto {fmtEuro(c.netto)}</div>
                        <div className="text-slate-400">btw {fmtEuro(c.btw)}</div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            title="Regel verwijderen"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={addLine}
                  className="flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700 px-1 pt-1"
                >
                  <Plus size={18} /> Regel toevoegen
                </button>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200 text-sm">
                  <div><span className="text-slate-500">Bruto:</span> <span className="font-bold text-slate-800">{fmtEuro(totals.bruto)}</span></div>
                  <div><span className="text-slate-500">BTW:</span> <span className="font-semibold text-slate-700">{fmtEuro(totals.btw)}</span></div>
                  <div><span className="text-slate-500">Netto:</span> <span className="font-semibold text-slate-700">{fmtEuro(totals.netto)}</span></div>
                </div>
                {isForeign && totals.bruto > 0 && rateNum > 0 && (
                  <div className="text-xs text-slate-400 pt-1">≈ {eur(brutoEur)} in EUR</div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input label="Datum" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                <Input label="Referentie" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="factuur-/bonnummer" />
              </div>
              {mode === "payment" && (
                <>
                  <Select label="Betaalmethode" options={methodOptions} value={method} onChange={(e) => setMethod(e.target.value)} />
                  <Select
                    label="Factuur (optioneel)"
                    options={[
                      { value: "", label: "Geen factuur koppelen" },
                      ...invoices.map((i) => ({ value: i.id, label: `${i.number} — € ${Number(i.total || 0).toFixed(2)}` })),
                    ]}
                    value={invoiceId}
                    onChange={(e) => setInvoiceId(e.target.value)}
                  />
                </>
              )}
              <Input label="Notities" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optioneel" />
            </div>
          )}

          {/* Import-bestaand-tab: kies regel + upload document */}
          {tab === "importExisting" && (
            <div className="space-y-4">
              <Select
                label={`Koppel document aan bestaande ${mode === "expense" ? "uitgave" : "betaling"}`}
                options={[
                  { value: "", label: `Kies een regel zonder document...` },
                  ...existingRows.map((r) => ({
                    value: r.id,
                    label: mode === "expense"
                      ? `${new Date(r.date).toLocaleDateString("nl-NL")} — ${r.description || r.merchant || "zonder omschrijving"} (€ ${Number(r.amountInclVat || 0).toFixed(2)})`
                      : `${new Date(r.date).toLocaleDateString("nl-NL")} — ${r.reference || r.invoice?.number || "betaling"} (€ ${Number(r.amount || 0).toFixed(2)})`,
                  })),
                ]}
                value={existingId}
                onChange={(e) => setExistingId(e.target.value)}
              />
              {existingRows.length === 0 && (
                <p className="text-sm text-slate-500">Er zijn geen regels zonder document. Alle regels hebben al een document gekoppeld.</p>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle size={22} className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Acties */}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="ghost" onClick={close} disabled={saving}>Annuleren</Button>
            {tab === "scan" && (
              <Button onClick={() => submitCreate(false)} loading={saving} disabled={!!quotaLow}>
                <ScanLine size={20} /> Scannen &amp; opslaan
              </Button>
            )}
            {tab === "importNew" && (
              <Button onClick={() => submitCreate(true)} loading={saving}>
                <Plus size={20} /> Importeren &amp; opslaan
              </Button>
            )}
            {tab === "importExisting" && (
              <Button onClick={submitAttachExisting} loading={saving}>
                <Upload size={20} /> Document koppelen
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <ContactFormModal
        isOpen={showContactModal}
        defaultType={defaultContactType}
        onClose={() => setShowContactModal(false)}
        onSaved={(c) => {
          setShowContactModal(false);
          api.get("/api/contacts").then((res) => setContacts(res.data)).then(() => setContactId(c.id));
        }}
      />
    </>
  );
}